import {computed, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {map, take, takeUntil, withLatestFrom} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {DataSourcePreviewApi} from '../api/data-source-preview.api';
import {PreviewDataSourceInfo} from '../../../../data-source/models/preview-data-source-info.model';
import {DataSourceRowNumber} from '../../../../data-source/models/data-source-row-number.type';
import {PreviewColumns} from '../../../../data-source/models/preview-columns.model';
import {DataSourcePreviewState} from '../state/data-source-preview.state';
import {DataSource} from '../../../../data-source/models/data-source.modol';
import {ToastrWrapperService} from '../../../services/toastr-wrapper.service';
import {SubmitType} from '../../../models/submit.type';
import {ColumnsType} from '../../../../data-source/models/columns-type.model';
import {IDataSourcePreview, OracleBodyReq} from '../interfaces/i-data-source-preview';
import {Connection} from '../../../../main/modules/management/models/Connection.model';
import {ConnectionTables} from '../../../models/connection-tables.model';
import {SqlServerConnection} from '../../../../data-source/models/sql-server-connection.model';
import {OracleConnection} from '../../../../data-source/models/oracle-connection.model';
import { MessageDialogComponent } from '../../message-dialog/message-dialog.component';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { ReplaceDatasourceConfirmDialogComponent } from
  '../../../../data-source/components/dialogs/replace-datasource-confirm-dialog/replace-datasource-confirm-dialog.component';
import { DataSourceFacade } from '../../../../data-source/facade/data-source.facade';
import {IncrementalSettingModel} from "../../../../data-source/models/incremental-setting.model";
import {ConnectionType} from "../../../../main/modules/management/models/connection.type";
import { ErrorUploadingDataSourceModalComponent } from '../components/error-uploading-data-source-modal/error-uploading-data-source-modal.component';
import { DataSourceUploadProgressService } from '../../../../data-source/services/data-source-upload-progress.service';

@Injectable()
export class DataSourcePreviewFacade implements IDataSourcePreview {

  // payam-refact : avoid directly calling datasource facade here;
  constructor(
    private dataSourcePreviewApi: DataSourcePreviewApi,
    private dataSourcePreviewState: DataSourcePreviewState,
    private dataSourceFacade: DataSourceFacade,
    private toastr: ToastrWrapperService,
    private dialogService: NbDialogService,
    private progressService: DataSourceUploadProgressService,
    private router: Router) { }
    readonly state = computed(() => ({
      isDatasourceAdded: this.dataSourcePreviewState.isDatasourceAdded(),
    }));
  
  loadDataSources(workspaceId: string, searchKey: string, column: string, ascending: boolean, pageNumber: number,
                  pageSize: number, needCount: boolean): void {
    this.dataSourcePreviewApi
      .getDataSources(workspaceId, searchKey, column, ascending, pageNumber, pageSize, needCount)
      .pipe(map(res => res.data)).subscribe(
        (res) => {
          this.dataSourcePreviewState.setPageNumber(pageNumber);
          this.dataSourcePreviewState.updateDataSources(res.pageData);
          if (needCount) {
            this.dataSourcePreviewState.setTotalPages(res.totalPages);
          }
        },
        () => { },
        () => { },
      );
  }
  addDataSourceToDashboard(
    workspaceId: string,
    dataSourceId: string,
    dashboardId: string
  ): void {
  
    this.setSubmitState('submitting');
  
    this.dataSourcePreviewApi
      .addDataSourceToDashboard(workspaceId, dashboardId, dataSourceId)
      .subscribe(
        () => {
          this.toastr.showSuccess('عملیات باموفقیت انجام شد.');
          this.dataSourcePreviewState.setIsDatasourceAdded(true);
        },
        () => {
          this.toastr.showError('افزودن منبع‌داده به داشبورد ناموفق بود.');
        },
        () => {
          this.setSubmitState('submitted');
        }
      );
  }
  

  getDataSources$(): Observable<Partial<DataSource>[]> {
    return this.dataSourcePreviewState.getDataSources$();
  }

  loadFileOrURLPreviewTableContent(workspaceId: string,
                                   previewDataSourceInfo: PreviewDataSourceInfo,
                                   rowNumber: DataSourceRowNumber,sheetName: string = undefined): void {
    this.dataSourcePreviewApi.getFilePreviewTableContent(workspaceId, previewDataSourceInfo,rowNumber,sheetName).
    pipe(map((res: any) => res.data)).subscribe(
      (tableContent: PreviewColumns) => {
        this.dataSourcePreviewState.updatePreviewTableContent(tableContent);
        this.dataSourcePreviewState.updateErrorTableContent(false);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.dataSourcePreviewState.updatePreviewTableContent(null);
        this.dataSourcePreviewState.updateErrorTableContent(true);
      },
    );
  }

  loadFileReplacementTableContent(workspaceId: string, previewDataSourceInfo: PreviewDataSourceInfo,
    rowNumber: DataSourceRowNumber, selectedSheetName: string,
    dataSourceWarnings: string[]): void {
    this.dataSourcePreviewApi.getReplacementDataSourcePreviewData(workspaceId, previewDataSourceInfo,
      rowNumber, selectedSheetName).subscribe(
        (response) => {
          const tableContent: PreviewColumns = {
            data: response.data.data,
            columnWarnings: response.data.warnings, dataSourceWarnings,
          };
          this.dataSourcePreviewState.updatePreviewTableContent(tableContent);
          this.dataSourcePreviewState.updateErrorTableContent(false);
        },
        (errorRes) => {
          if (errorRes.error.showPopUp) {
            this.dialogService.open(MessageDialogComponent,
              {
                closeOnBackdropClick: false,
                context: {
                  message: 'جایگذاری انجام نشد. پس از بررسی جدول، دوباره امتحان کنید.',
                  errorMessage: errorRes.error.message, icon: 'error', title: 'ناموفق',
                },
              });
          } else {
            this.toastr.showError(errorRes.error.message);
          }
          this.dataSourcePreviewState.updatePreviewTableContent(null);
          this.dataSourcePreviewState.updateErrorTableContent(true);
        },
      );
  }

  loadSqlServerPreviewTableContent(workspaceId: string,
                                   previewDataSourceInfo: PreviewDataSourceInfo, rowNumber: DataSourceRowNumber): void {
    this.dataSourcePreviewApi.getSqlServerPreviewTableContent(workspaceId, previewDataSourceInfo,rowNumber).
    pipe(map((res: any) => res.data)).subscribe(
      (tableContent: PreviewColumns) => {
        this.dataSourcePreviewState.updatePreviewTableContent(tableContent);
        this.dataSourcePreviewState.updateErrorTableContent(false);
      },
      (errorRes) => {
        this.dataSourcePreviewState.updatePreviewTableContent(null);
        this.dataSourcePreviewState.updateErrorTableContent(true);

        if (errorRes.error.showPopUp) {
          this.dialogService.open(MessageDialogComponent,
            {
              closeOnBackdropClick: false,
              context: {
                message: errorRes.error.message, icon: 'error', title: 'خطا',
              },
            });
        } else {
          this.toastr.showError(errorRes.error.message);
        }
      },
    );
  }

  loadWebServicePreviewTableContent(
    workspaceId: string,
    previewDataSourceInfo: PreviewDataSourceInfo,
    rowNumber: DataSourceRowNumber,
  ): void {
    this.dataSourcePreviewApi
      .getWebServicePreviewTableContent(workspaceId, previewDataSourceInfo, rowNumber)
      .pipe(map((res: any) => res.data))
      .subscribe(
        (tableContent: PreviewColumns) => {
          this.dataSourcePreviewState.updatePreviewTableContent(tableContent);
        },
        (errorRes) => {
          this.dialogService.open(ErrorUploadingDataSourceModalComponent)
          this.toastr.showError(errorRes.error.message);
          this.setSubmitState('submitted');
        },
      );
  }

  loadOraclePreviewTableContent(workspaceId: string,
                                    previewDataSourceInfo: PreviewDataSourceInfo,rowNumber: DataSourceRowNumber): void {
      this.dataSourcePreviewApi.getOraclePreviewTableContent(workspaceId, previewDataSourceInfo,rowNumber).
      pipe(map((res: any) => res.data)).subscribe(
          (tableContent: PreviewColumns) => {
              this.dataSourcePreviewState.updatePreviewTableContent(tableContent);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );

  }

  createDataSourceTable(previewDataSourceInfo: PreviewDataSourceInfo, workspaceId: string,
    columnsTypes: ColumnsType, sheetName: string = undefined): void {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.createDataSourceTable(previewDataSourceInfo, workspaceId, columnsTypes, sheetName,
      this.dataSourcePreviewState.addToDashboard, this.dataSourcePreviewState.dashboardId).subscribe(
        (response) => {
          if (!response.data.hasError) {
            if (response.data.completionStatus === 'InProgress' || response.data.completionStatus === 'ReadyBySchedule') {
              // if (response.data.isDuplicated) {
              //   this.toastr.showInfo(`منبع داده "${response.data.name}" در حال تکرار است.`);
              // } else {
                this.toastr.showInfo(`منبع داده "${response.data.name}" در حال بارگذاری است.`);
              // }
              this.progressService.closeModal();
              this.progressService.forceOpenModal();
              this.progressService.toggle(true)
            }
            else if (response.data.completionStatus === 'None') {
              this.progressService.closeModal();
              this.progressService.forceOpenModal();
              this.progressService.toggle(true)
              this.toastr.showSuccess(`منبع داده ساخته شد.`);
            } else if(response.data.completionStatus === 'Completed') {
              // if (response.data.isDuplicated) {
              //   this.toastr.showSuccess(`تکرار منبع داده "${response.data.name}" کامل شد.`);
              // } else {
                // this.toastr.showSuccess(`بارگذاری منبع داده "${response.data.name}" کامل شد.`);
              // }
            }
            this.dataSourcePreviewState.setIsDatasourceAdded(true)
            this.setSubmitState('submitted');
            const dataSourceId = response.data.dataSourceId;
            if (!this.dataSourcePreviewState.addToDashboard) {
              this.router.navigate(['./datasource', workspaceId, dataSourceId]);
            }

          } else {
            this.setSubmitState('notSubmitted');
            // this.toastr.showError(`بارگذاری منبع داده "${response.data.name}" با مشکل مواجه شد.`);
            this.dialogService.open(MessageDialogComponent,
              {
                closeOnBackdropClick: false,
                context: {
                  message: 'جایگذاری انجام نشد. پس از بررسی جدول، دوباره امتحان کنید.',
                  errorsList: response.data.errorMessages,
                  icon: 'error',
                  errorsListTitle: 'مقادیر ناهماهنگ(' + response.data.errorMessages.length + ')',
                  title: 'ناموفق',
                },
              });
          }
        },
        (errorRes) => {
          if (errorRes.error.showPopUp) {
            this.dialogService.open(MessageDialogComponent,
              {
                closeOnBackdropClick: false,
                context: {
                  message: 'بارگذاری داده با خطا مواجه شد. ممکن است به‌دلیل ناسازگاری داده‌ها یا محدودیت‌های سیستم (مانند تعداد ستون‌ها) باشد.\n' +
                    'لطفاً تنظیمات را بررسی کرده و دوباره تلاش کنید.'
                  , errorMessage: errorRes.error.message, icon: 'error', title: 'خطا',
                },
              });
          } else {
            this.toastr.showError('منبع داده ساخته نشد.');
          }
          this.setSubmitState('notSubmitted');
        },
      );
  }

  makeNewDashboard(newDashboard: any, workspaceId: string) {
    this.setSubmitState('submitting');
    newDashboard.dashboardDesignSettings = `{"backgroundColor": "#F8F8F8",
                                       "showTitle": true,
                                       "titleBackgroundColor": "#FFFFFF",
                                       "titleColor": "#4D4D4D",
                                       "titleDividerLineColor": "#EDEDED",
                                       "titleBorderColor": "#E8E8E8",
                                       "titleFontFamily": "iranyekan",
                                       "titleFontSize": 24,
                                       "toggleFooter": false,
                                       "autoRefreshEnabled": false,
                                       "textFooterColor": "#4D4D4D",
                                       "textFooterAlignment": "Right",
                                       "footerBackground": "#FFFFFF",
                                       "footerBorder": "#FFFFFF",
                                       "footerDividerColor": "#E8E8E8",
                                       "showCreator": true,
                                       "showLastModifiedDataSourceUpdated": false,
                                       "showLastModifiedDashboardUpdated": false,
                                       "showView": false,
                                       "showUsersCountAccessToDashboard": false,
                                       "showCustomText": false,
                                       "footerCustomText": "",
                                       "chipsColor": "#00000099",
                                       "chipsBackgroundColor": "#dddddd",
                                       "version": "0.0.1"}`;
    this.dataSourcePreviewApi.makeNewDashboard(newDashboard).subscribe(
      (res) => {
        this.router.navigate([`/dashboard/view/${workspaceId}/${res.data.dashboardId}`]);
        this.toastr.showSuccess('داشبورد ساخته شد.');
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes?.error?.message||'خطا در ساخت داشبورد');
        this.setSubmitState('notSubmitted');
      },
    );
  }


  getPreviewTableContent$(): Observable<PreviewColumns> {
    return this.dataSourcePreviewState.getPreviewTableContent$();
  }

  deletePreviewConnection(previewDataSourceInfo: PreviewDataSourceInfo) {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.deletePreviewConnection(previewDataSourceInfo).subscribe(
      () => {
        this.toastr.showSuccess();
        this.setSubmitState('submitted');
      },
      () => {
        this.toastr.showError();
        this.setSubmitState('notSubmitted');
      },
    );
  }

  deletePreviewReplacementConnection(previewDataSourceInfo: PreviewDataSourceInfo): void {
      this.setSubmitState('submitting');
      this.dataSourcePreviewApi.deletePreviewReplacementConnection(previewDataSourceInfo).subscribe(
          () => {
              this.setSubmitState('submitted');
          },
          () => {
              this.toastr.showError();
              this.setSubmitState('notSubmitted');
          },
      );
  }

  loadExcelSheetsNamesPreview(dataSourceId: string, filePath: string): void {
    this.dataSourcePreviewApi.getExcelSheetsNamesPreview(dataSourceId,filePath).subscribe(
      (res) => {
        this.dataSourcePreviewState.setExcelSheetNames(res.data.sheetNames);
      },
    );
  }

  getExcelSheetNames$(): Observable<string[]> {
    return this.dataSourcePreviewState.getExcelSheetNames$();
  }

  createWebServiceConnection(workspaceId: string, name: string, address: string, dataTypes: string, methodTypes: string,
                             authenticationTypes: string, body: string, username, password: string,
                             requestParameters: {name: string; value: string; type: string }[], requestHeaders: {name: string; value: string; }[], jsonBody: {name: string; value: string; }[]): void {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.createWebServiceConnection(workspaceId, name, address, dataTypes, methodTypes,
      authenticationTypes, body, username, password, requestParameters, requestHeaders, jsonBody).pipe(map(res => res.data)).subscribe(
      (dataSourceInfo: PreviewDataSourceInfo) => {
        dataSourceInfo.fileName = name;
        this.dataSourcePreviewState.updatePreviewDataSourceInfo(dataSourceInfo);
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('notSubmitted');
      },
    );
  }

  loadOracleConnection(workspaceId: string, dataSourceId: string): void {
      this.dataSourcePreviewApi.getOracleConnection(workspaceId,dataSourceId).pipe(map(res => res.data)).subscribe(
          (oracleConnection: OracleConnection) => {
              this.dataSourcePreviewState.updateOracleConnection(oracleConnection);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  updateOracleConnection(body: Partial<OracleBodyReq>): void {
      this.setSubmitState('submitting');
      this.dataSourcePreviewApi.updateOracleConnection(body).subscribe(
          () => {
              this.setSubmitState('submitted');
              this.toastr.showSuccess();
          },
          (errorRes) => {
              this.setSubmitState('notSubmitted');
              this.toastr.showError(errorRes.error.message);
          },
          () => {},
      );

  }

  getOracleConnection$(): Observable<Partial<OracleConnection>> {
      return this.dataSourcePreviewState.getOracleConnection$();
  }

  createOracleConnection(body: Partial<OracleBodyReq>): void {
      this.setSubmitState('submitting');
      this.dataSourcePreviewApi.createOracleConnection(body).pipe(map(res => res.data)).subscribe(
          (dataSourceInfo: PreviewDataSourceInfo) => {
              dataSourceInfo.fileName = body.name;
              this.dataSourcePreviewState.updatePreviewDataSourceInfo(dataSourceInfo);
              this.toastr.showSuccess();
              this.setSubmitState('submitted');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
      );
  }

  loadSqlServerConnection(workspaceId: string, dataSourceId: string): void {
      this.dataSourcePreviewApi.getSqlServerConnection(workspaceId,dataSourceId).pipe(map(res => res.data)).subscribe(
          (sqlServerConnection: SqlServerConnection) => {
              this.dataSourcePreviewState.updateSqlServerConnection(sqlServerConnection);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  loadIncrementalSetting(workspaceId: string, dataSourceId: string): void {
    this.dataSourcePreviewApi.getIncrementalSetting(workspaceId,dataSourceId).pipe(map(res => res.data.incrementalSetting)).subscribe(
      (incrementalSetting: IncrementalSettingModel) => {
        this.dataSourcePreviewState.updateIncrementalSetting(incrementalSetting);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateIncrementalSetting(workspaceId: string, dataSourceId: string, id: string, updated: string,
                           deleted: string, timeMode: string, count: number, isIncremental: boolean): void {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.updateIncrementalSetting(workspaceId, dataSourceId, id, updated, deleted, timeMode, count, isIncremental).subscribe(
      () => {
        this.setSubmitState('submitted');
        if(isIncremental)
        this.toastr.showSuccess();
      },
      (errorRes) => {
        this.setSubmitState('notSubmitted');
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  createSqlServerConnection(workspaceId: string, name: string, serverAddress: string, portNumber: string,
                            dbName: string, windowsAuth: boolean, username: string, password: string,
                            commandTimeout: number, sqlStatement: string): void {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.createSqlServerConnection(workspaceId, name, serverAddress, portNumber, dbName,
        windowsAuth, username, password, commandTimeout, sqlStatement).pipe(map(res => res.data)).subscribe(
      (dataSourceInfo: PreviewDataSourceInfo) => {
        dataSourceInfo.fileName = name;
        this.dataSourcePreviewState.updatePreviewDataSourceInfo(dataSourceInfo);
        this.toastr.showSuccess();
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('notSubmitted');
      },
    );
  }

  updateSqlServerConnection(workspaceId: string, dataSourceId: string, connectionId: string,
                            name: string, serverAddress: string, portNumber: string, dbName: string,
                            windowsAuth: boolean, username: string, password: string,numberOfRowLimited: string, hasRowLimited: boolean, commandTimeout?: number,
                            sqlStatement?: string): void {
      this.setSubmitState('submitting');
      this.dataSourcePreviewApi.updateSqlServerConnection(workspaceId, dataSourceId, connectionId, name,
          serverAddress, portNumber, dbName, windowsAuth, username, password, numberOfRowLimited, hasRowLimited, commandTimeout,
          sqlStatement).subscribe(
          () => {
              this.setSubmitState('submitted');
              this.toastr.showSuccess();
          },
          (errorRes) => {
              this.setSubmitState('notSubmitted');
              this.toastr.showError(errorRes.error.message);
          },
          () => {},
      );
  }

  convertNewConnectionToAvailableDBConnection(workspaceId: string, dataSourceId: string, connectionId: string,
                            payload: {
                              tableId: string;
                              tableName: string;
                              dbConnectionId: string;
                              dataSourceName: string;
                              connectionType: ConnectionType;
                              hasRowLimited: boolean;
                              numberOfRowLimited: string;
                              hasTopN: boolean;
                              numberOfTopN: string;
                              windowsAuth: boolean;
                              serverAddress: string;
                              dbName: string;
                              password: string;
                              commandTimeout: number;
                              connectionName: string;
                              portNumber: string;
                              sqlStatement: string;
                              username: string;
                            }): void {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.convertNewConnectionToAvailableDBConnection(workspaceId, dataSourceId, connectionId,
      payload).subscribe(
      () => {
        this.setSubmitState('submitted');
        this.toastr.showSuccess();
      },
      (errorRes) => {
        this.setSubmitState('notSubmitted');
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  convertAvailableDBConnectionToNewConnection(workspaceId: string, dataSourceId: string, connectionId: string,
                                              payload: {
                                                dataSourceName: string;
                                                connectionName: string;
                                                serverAddress: string;
                                                portNumber: string;
                                                dbName: string;
                                                windowsAuth: boolean;
                                                username: string;
                                                password: string;
                                                commandTimeout: number;
                                                sqlStatement: string;
                                              }): void {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.convertAvailableDBConnectionToNewConnection(workspaceId, dataSourceId, connectionId,
      payload).subscribe(
      () => {
        this.setSubmitState('submitted');
        this.toastr.showSuccess();
      },
      (errorRes) => {
        this.setSubmitState('notSubmitted');
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  getSqlServerConnection$(): Observable<Partial<SqlServerConnection>> {
      return this.dataSourcePreviewState.getSqlServerConnection$();
  }

  getIncrementalSetting$(): Observable<Partial<IncrementalSettingModel>> {
    return this.dataSourcePreviewState.getIncrementalSetting$();
  }

  updateWorkspaceId(workspaceId: string): void {
    this.dataSourcePreviewState.updateWorkspaceId(workspaceId);
  }

  getWorkspaceId$(): Observable<string> {
    return this.dataSourcePreviewState.getWorkspaceId$();
  }

  getPageNumber$(): Observable<number> {
    return this.dataSourcePreviewState.getPageNumber$();
  }

  getTotalPages$(): Observable<number> {
    return this.dataSourcePreviewState.getTotalPages$();
  }

  setSubmitState(submitted: SubmitType): void {
    this.dataSourcePreviewState.setSubmitState(submitted);
  }

  getSubmitState$(): Observable<SubmitType> {
    return this.dataSourcePreviewState.getSubmitState$();
  }

  getErrorTableContent$(): Observable<boolean> {
    return this.dataSourcePreviewState.getErrorTableContent$();
  }

  uploadFile(file: File, workspaceId: string, uploadType: string): void {
    this.setSubmitState('submitting');
    const uploadFile = new FormData();
    uploadFile.append('formFile', file, file.name);
    this.dataSourcePreviewApi.uploadFile(workspaceId, uploadType, uploadFile).pipe(map(res => res.data))
      .subscribe(
        (dataSource: PreviewDataSourceInfo) => {
          dataSource.fileName = file.name;
          this.dataSourcePreviewState.updatePreviewDataSourceInfo(dataSource);
          this.setSubmitState('submitted');
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
          this.setSubmitState('notSubmitted');
        },
        () => { },
      );
  }

  uploadFileFromURL(url: string, workspaceId: string): void {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.uploadFileFromURL(workspaceId, url).pipe(map(res => res.data))
      .subscribe(
        (dataSource: PreviewDataSourceInfo) => {
          this.dataSourcePreviewState.updatePreviewDataSourceInfo(dataSource);
          this.setSubmitState('submitted');
          this.toastr.showSuccess();
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
          this.setSubmitState('notSubmitted');
        },
        () => { },
      );
  }

  getPreviewDataSourceInfo$(): Observable<PreviewDataSourceInfo> {
    return this.dataSourcePreviewState.getPreviewDataSourceInfo$();
  }

  uploadReplaceDataSource(file: File, uploadType: string, workspaceId: string, dataSourceId: string): void {
      this.setSubmitState('submitting');
      const uploadFile = new FormData();
      uploadFile.append('formFile', file, file.name);
      this.dataSourcePreviewApi.replaceDataSource(uploadFile, uploadType, workspaceId).pipe(map(res => res.data))
          .subscribe(
          (dataSource: PreviewDataSourceInfo) => {
              dataSource.fileName = file.name;
              dataSource.dataSourceId = dataSourceId;
              this.dataSourcePreviewState.updatePreviewDataSourceInfo(dataSource);
              this.setSubmitState('submitted');
              this.toastr.showSuccess();
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
          () => { },
      );
  }

  setAddToDashboard(addToDashboard: boolean,dashboardId: string): void {
    this.dataSourcePreviewState.setAddToDashboard(addToDashboard);
    this.dataSourcePreviewState.setDashboardId(dashboardId);
  }

  loadUserDataBaseConnections(workspaceId: string): void {
      this.dataSourcePreviewApi.getUserDataBaseConnections(workspaceId).subscribe(
          (res) => {
              this.dataSourcePreviewState.setConnections(res.data);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getConnections$(): Observable<Partial<Connection>[]> {
      return this.dataSourcePreviewState.getConnections$();
  }

  loadDataBaseConnectionTables(workspaceId: string, connectionId: string): void {
      this.dataSourcePreviewApi.getDataBaseConnectionTables(workspaceId, connectionId).subscribe(
          (res) => {
              this.dataSourcePreviewState.updateConnectionTables(res.data);
          },
          (errorRes) => {
            this.dataSourcePreviewState.updateConnectionTables(undefined);
            this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getConnectionTables$(): Observable<Partial<ConnectionTables>> {
      return this.dataSourcePreviewState.getConnectionTables$();
  }

  resetConnectionTables(): void {
      this.dataSourcePreviewState.updateConnectionTables(null);
  }

  createSqlServerDataBaseConnection(tableId: string, dbConnectionId: string, workspaceId: string,
    dataSourceName: string, hasRowLimited: boolean, numberOfRowLimited: string,
    hasTopN: boolean, numberOfTopN: string): void {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.createSqlServerDataBaseConnection(tableId, dbConnectionId, workspaceId, dataSourceName,
      hasRowLimited, numberOfRowLimited, hasTopN, numberOfTopN)
      .subscribe(
        (res) => {
          this.setSubmitState('submitted');
          this.dataSourcePreviewState.updatePreviewDataSourceInfo(res.data);
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
          this.setSubmitState('notSubmitted');
        },
      );
  }

  updateSqlServerDataBaseConnection(workspaceId: string, dataSourceId: string, connectionId: string,
    dbConnectionId: string, dataSourceName: string, tableId: string, hasRowLimited: boolean, numberOfRowLimited: string,
    hasTopN: boolean, numberOfTopN: string): void {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.updateSqlServerDataBaseConnection(workspaceId, dataSourceId, connectionId,
      dbConnectionId, dataSourceName, tableId, hasRowLimited, numberOfRowLimited, hasTopN, numberOfTopN).subscribe(
        () => {
          this.setSubmitState('submitted');
          this.toastr.showSuccess();
        },
        (errorRes) => {
          this.setSubmitState('notSubmitted');
          this.toastr.showError(errorRes.error.message);
        });
  }

  replaceExcellWithSqlServerConnection(
    currentDataSourceName: string, datasourceId: string,
    workspaceId: string, name: string, serverAddress: string,
    portNumber: string, dbName: string, windowsAuth: boolean,
    username: string, password: string, commandTimeout: number,
    sqlStatement: string): void {

      this.setSubmitState('submitting');
      this.dataSourcePreviewApi.createReplacementSqlServerConnection(
                      datasourceId, workspaceId, name, serverAddress,
                      portNumber, dbName, windowsAuth,
        username, password, commandTimeout, sqlStatement)
        .subscribe(
          (res: any) => {
            this.setSubmitState('submitted');
            this.dataSourcePreviewState.updatePreviewDataSourceInfo(res?.data);
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
            this.setSubmitState('notSubmitted');
          },
      );

                  // if (closeRes === 'decline'){}
                // },
              // );
  }

  replaceExcellWithSqlServerDataBaseConnection(
    currentDataSourceName: string,
    dataSourceId: string, tableId: string,
    dbConnectionId: string, workspaceId: string,
    dataSourceName: string, hasRowLimited: boolean,
    numberOfRowLimited: string, hasTopN: boolean,
    numberOfTopN: string): void {

      this.setSubmitState('submitting');

      let hasDashboards = false;
      this.dataSourceFacade
        .getUsedDashboards$()
        .subscribe(
          dashboards => {
            if(dashboards.length > 0){
              hasDashboards = true;
            }

            this.dataSourcePreviewApi.createReplacementSqlServerDBConnection(
              dataSourceId, tableId, dbConnectionId, 
              workspaceId, dataSourceName, hasRowLimited,
              numberOfRowLimited, hasTopN, numberOfTopN)
              .subscribe(
                (res: any) => {
                  this.setSubmitState('submitted');
                  this.dataSourcePreviewState.updatePreviewDataSourceInfo(res?.data);
                },
                (errorRes) => {
                  this.toastr.showError(errorRes.error.message);
                  this.setSubmitState('notSubmitted');
                },
            );
          }
      );

  }

  checkUpdateDataSourceData(workspaceId: string, previewDataSourceInfo: PreviewDataSourceInfo,
                            rowNumber: DataSourceRowNumber, selectedSheetName: string): void {
      this.dataSourcePreviewApi.checkUpdateDataSourceData(workspaceId, previewDataSourceInfo, rowNumber,
          selectedSheetName).subscribe(
          (response) => {
              this.loadFileReplacementTableContent(workspaceId, previewDataSourceInfo, rowNumber, selectedSheetName,
                  response?.data.hasError ? response.data.warnings : []);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  loadReplacementDataBaseConnectionTables(workspaceId: string, previewDataSourceInfo: PreviewDataSourceInfo, rowNumber: DataSourceRowNumber, selectedSheetName: string){
    this.dataSourcePreviewApi.getReplacementSqlServerPreviewData(workspaceId, previewDataSourceInfo.connectionId, rowNumber)
    .subscribe(
      (response) => {
        const dataSourceWarnings: string[] = []
        const tableContent: PreviewColumns = {
            data: response.data.data,
            columnWarnings: response.data.warnings, dataSourceWarnings,
          };
        this.dataSourcePreviewState.updatePreviewTableContent(tableContent);
        this.dataSourcePreviewState.updateErrorTableContent(false);
      },
      (err) => {
        this.dataSourcePreviewState.updatePreviewTableContent(undefined);
        this.toastr.showError(err.error.message);
      }
    )
  }

  deleteReplacementSQLConnectionAndRevokeDataSource(previewDataSourceInfo: PreviewDataSourceInfo) {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.
      deleteReplacementSQLConnectionAndRevokeDataSource(previewDataSourceInfo.dataSourceId, previewDataSourceInfo.connectionId)
      .subscribe(
        () => {
          this.setSubmitState('submitted');
        },
        () => {
          this.toastr.showError();
          this.setSubmitState('notSubmitted');
        },
    );
  }

  replaceExcelWithSqlServerDataSource(workspaceId: string, dataSourceId: string, name: string) {
    this.setSubmitState('submitting');
    this.dataSourcePreviewApi.replaceExcelWithSqlServerDataSource(workspaceId, dataSourceId, name)
    .subscribe(
      (res: any) => {
        this.toastr.showSuccess('منبع داده با موفقیت جایگزین شد.');
        this.setSubmitState('submitted');
      },
      (err) => {
        this.toastr.showError('منبع داده ساخته نشد.');
        this.setSubmitState('notSubmitted');
      }
    )
    
  }
}
